/**
 * The phone recap video: Present View redrawn on a 720×1280 canvas.
 *
 * Phones can't capture their own screen (no getDisplayMedia), so on them
 * recorder.js records this canvas instead. It only DRAWS what the app already
 * decided: stage frames come from the draw views (`onFrame`), results from the
 * store. It never picks anyone, and `Math.random` is used for confetti only.
 *
 * User text goes through `fillText`, never HTML. Avatars are initials, because
 * cross-origin TikTok images would taint the canvas and blank the recording;
 * only an uploaded `data:` creator avatar is drawn as a picture.
 */
import { ensureFonts, wrapText } from '../certificate.js';
import { ringSweep, scrollOffset, visibleSlotRows } from '../lib/recap-scene.js';
import { initials, prefersReducedMotion, themeVar } from '../ui/dom.js';

export const WIDTH = 720;
export const HEIGHT = 1280;
const FPS = 30;
const EMOJI = "'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji'";
const DISPLAY = `'Outfit Variable', 'Noto Sans Myanmar', system-ui, sans-serif, ${EMOJI}`;
const BODY = `'Inter Variable', 'Noto Sans Myanmar', system-ui, sans-serif, ${EMOJI}`;
const TAU = Math.PI * 2;

const MARGIN = 40;
const CARD = { x: MARGIN, y: 430, w: WIDTH - MARGIN * 2, h: 690, head: 84 };
const SLOT_ROW = 170;
// Below the tier heading and above the footer; every draw mode and the reveal centre on it.
const STAGE_TOP = 236;
const STAGE_MID = 725;
const REVEAL_FADE_MS = 450;

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
};

function fitFont(ctx, text, weight, family, startPx, minPx, maxWidth) {
  let px = startPx;
  for (; px > minPx; px -= 2) {
    ctx.font = `${weight} ${px}px ${family}`;
    if (ctx.measureText(text).width <= maxWidth) return px;
  }
  ctx.font = `${weight} ${minPx}px ${family}`;
  return minPx;
}

const oneLine = (ctx, text, maxWidth) => wrapText(ctx, String(text ?? ''), maxWidth, 1)[0] ?? '';

export function createCompositor({ wheelCanvas }) {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');

  let t = {};
  let raf = 0;
  let lastFrameAt = 0;
  let phase = null;
  let phaseSince = 0;
  let stage = null;
  let reveal = null;
  let revealSince = 0;
  let tier = null;
  let creator = { title: '', avatar: '' };
  let intro = null;
  let winners = null;
  let avatarImage = null;
  let particles = [];

  function refreshTheme() {
    t = {
      bg: themeVar('--tk-bg') || '#0b0d12',
      bg2: themeVar('--tk-bg-2') || '#151a26',
      text: themeVar('--tk-text') || '#ffffff',
      muted: themeVar('--tk-muted') || '#999999',
      accent: themeVar('--tk-accent') || '#7c9cff',
      accent2: themeVar('--tk-accent-2') || '#b38cff',
      accentFg: themeVar('--tk-accent-fg') || '#0b0d12',
      confetti: themeVar('--tk-confetti', { list: true }),
    };
    if (!t.confetti.length) t.confetti = [t.accent, t.accent2, '#ffffff'];
  }

  /* ---------------------------- setters ---------------------------- */
  const api = {
    canvas,

    /** Loads the fonts and paints one frame, so a stream started now is never blank. */
    async prepare(sampleText = '') {
      refreshTheme();
      await ensureFonts(`TokDraw မြန်မာ ${sampleText}`.slice(0, 600));
      api.render();
    },

    setPhase(next) {
      phase = next;
      phaseSince = performance.now();
    },
    setStage(frame) {
      stage = frame;
    },
    setReveal(record) {
      reveal = record;
      revealSince = performance.now();
    },
    setTier(next) {
      tier = next;
    },
    setCreator(next) {
      creator = next;
      avatarImage = null;
      if (next.avatar?.startsWith('data:image/')) {
        const image = new Image();
        image.onload = () => {
          if (creator === next) avatarImage = image;
        };
        image.src = next.avatar;
      }
    },
    setIntro(scene) {
      intro = scene && layoutIntro(scene);
    },
    setWinners(scene) {
      winners = scene && layoutWinners(scene);
    },
    refreshTheme,

    burst() {
      const count = prefersReducedMotion() ? 50 : 150;
      for (let i = 0; i < count; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * 2.2;
        const speed = 500 + Math.random() * 700;
        particles.push({
          x: WIDTH / 2,
          y: HEIGHT * 0.55,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: 8 + Math.random() * 10,
          spin: Math.random() * TAU,
          color: t.confetti[i % t.confetti.length],
        });
      }
    },

    start() {
      if (raf) return;
      refreshTheme();
      lastFrameAt = 0;
      const frame = (now) => {
        raf = requestAnimationFrame(frame);
        if (now - lastFrameAt < 1000 / FPS - 2) return; // the stream samples at FPS anyway
        const dt = lastFrameAt ? Math.min(0.1, (now - lastFrameAt) / 1000) : 0;
        lastFrameAt = now;
        updateParticles(dt);
        api.render(now);
      };
      raf = requestAnimationFrame(frame);
    },
    stop() {
      cancelAnimationFrame(raf);
      raf = 0;
    },

    render(now = performance.now()) {
      if (!t.bg) refreshTheme();
      drawBackground();
      if (phase === 'intro') drawIntro(now);
      else if (phase === 'winners') drawWinners(now);
      else drawStage(now);
      drawParticles();
      drawFooter();
    },
  };

  /* --------------------------- layouts ----------------------------- */
  // Text is measured once per scene, not on every frame.
  const textWidth = CARD.w - 150;

  function layoutIntro(scene) {
    const rows = scene.rows.map((row) => {
      setBodyFont(26, 700);
      const name = oneLine(ctx, row.name, textWidth);
      setBodyFont(22, 400);
      return { ...row, shownName: name, shownComment: oneLine(ctx, row.comment, textWidth) };
    });
    return { ...scene, rows };
  }

  function layoutWinners(scene) {
    const items = scene.items.map((item) => {
      if (item.kind === 'group') {
        setBodyFont(26, 800);
        return { ...item, shown: oneLine(ctx, item.title, textWidth - 60), height: 64 };
      }
      setBodyFont(26, 700);
      const name = oneLine(ctx, `${item.name}  ${item.handle}`, textWidth);
      setBodyFont(22, 400);
      return { ...item, shownName: name, shownComment: oneLine(ctx, item.comment, textWidth), height: 98 };
    });
    return { ...scene, items };
  }

  function setBodyFont(px, weight = 400) {
    ctx.font = `${weight} ${px}px ${BODY}`;
  }

  /* ---------------------------- drawing ---------------------------- */
  function drawBackground() {
    const bg = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
    bg.addColorStop(0, t.bg2);
    bg.addColorStop(1, t.bg);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
    for (const [x, y, r, color] of [
      [100, 160, 420, t.accent],
      [WIDTH - 80, HEIGHT - 200, 460, t.accent2],
    ]) {
      const glow = ctx.createRadialGradient(x, y, 0, x, y, r);
      glow.addColorStop(0, `${color}44`);
      glow.addColorStop(1, `${color}00`);
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
  }

  function drawFooter() {
    ctx.textAlign = 'center';
    ctx.fillStyle = t.muted;
    setBodyFont(20, 600);
    ctx.fillText('Drawn fairly with TokDraw', WIDTH / 2, HEIGHT - 28);
  }

  function gradient(x0, x1) {
    const g = ctx.createLinearGradient(x0, 0, x1, 0);
    g.addColorStop(0, t.accent);
    g.addColorStop(1, t.accent2);
    return g;
  }

  function drawAvatar(cx, cy, r, label, picture = null) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, TAU);
    ctx.fillStyle = gradient(cx - r, cx + r);
    ctx.fill();
    if (picture) {
      ctx.clip();
      ctx.drawImage(picture, cx - r, cy - r, r * 2, r * 2);
    } else {
      ctx.fillStyle = t.accentFg;
      ctx.font = `800 ${Math.round(r * 0.8)}px ${DISPLAY}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, cx, cy + 2);
    }
    ctx.restore();
    ctx.textBaseline = 'alphabetic';
  }

  /** Avatar, "Giveaway by …" and the display name: the top of the intro and winners pages, as in Present View. */
  function drawCreatorHeader() {
    const y = 190;
    const r = 84;
    // Initials from the display name when there is one, like the page's avatar.
    drawAvatar(WIDTH / 2, y, r, initials(creator.sub || creator.title.replace(/^Giveaway by /, '')), avatarImage);
    ctx.fillStyle = t.text;
    ctx.textAlign = 'center';
    fitFont(ctx, creator.title, 800, DISPLAY, 44, 24, WIDTH - MARGIN * 2);
    ctx.fillText(creator.title, WIDTH / 2, y + r + 14 + 44 * 0.8);
    if (creator.sub) {
      ctx.fillStyle = t.muted;
      setBodyFont(26, 500);
      ctx.fillText(oneLine(ctx, creator.sub, WIDTH - MARGIN * 2), WIDTH / 2, 368);
    }
  }

  /**
   * A list card (header + clipped, auto-scrolling body) shared by the intro and winners pages.
   * With `hug`, a short list gets a card its own size instead of the full-height one.
   */
  function drawListCard({ title, total, items, tops, contentHeight, since, now, drawItem, empty, hug = false }) {
    const { x, y, w, head } = CARD;
    const h = hug && items.length ? Math.min(CARD.h, head + contentHeight + 12) : CARD.h;
    ctx.fillStyle = `${t.text}12`;
    roundRect(ctx, x, y, w, h, 28);
    ctx.fill();
    ctx.strokeStyle = `${t.accent}55`;
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.save();
    roundRect(ctx, x, y, w, h, 28);
    ctx.clip();
    ctx.fillStyle = gradient(x, x + w);
    ctx.fillRect(x, y, w, head);
    ctx.fillStyle = t.accentFg;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.font = `800 32px ${DISPLAY}`;
    ctx.fillText(title, x + 32, y + head / 2 + 1);
    ctx.textAlign = 'right';
    ctx.font = `700 26px ${BODY}`;
    ctx.fillText(`Total ${total}`, x + w - 32, y + head / 2 + 1);

    const bodyTop = y + head;
    const bodyHeight = h - head;
    ctx.beginPath();
    ctx.rect(x, bodyTop, w, bodyHeight);
    ctx.clip();
    if (!items.length) {
      ctx.textAlign = 'center';
      ctx.fillStyle = t.muted;
      ctx.font = `500 26px ${BODY}`;
      ctx.fillText(empty, x + w / 2, bodyTop + bodyHeight / 2);
    } else {
      const offset = scrollOffset(now - since, contentHeight, bodyHeight);
      for (let i = 0; i < items.length; i++) {
        const top = bodyTop + tops[i] - offset;
        if (top > bodyTop + bodyHeight || top + items[i].height < bodyTop) continue;
        drawItem(items[i], x, top, w);
      }
    }
    ctx.restore();
    ctx.textBaseline = 'alphabetic';
    return y + h;
  }

  function stack(items) {
    const tops = [];
    let y = 0;
    for (const item of items) {
      tops.push(y);
      y += item.height;
    }
    return { tops, contentHeight: y };
  }

  function drawIntro(now) {
    drawCreatorHeader();

    const scene = intro ?? { rows: [], total: 0 };
    const items = scene.rows.map((row) => ({ ...row, height: 92 }));
    const { tops, contentHeight } = stack(items);
    drawListCard({
      title: 'Participants',
      total: scene.total,
      items,
      tops,
      contentHeight,
      since: phaseSince,
      now,
      empty: 'No participants yet.',
      drawItem(row, x, top, w) {
        ctx.textBaseline = 'alphabetic';
        drawAvatar(x + 54, top + 46, 28, initials(row.name));
        ctx.textAlign = 'left';
        ctx.fillStyle = row.won ? t.accent : t.text;
        setBodyFont(26, 700);
        ctx.fillText(row.won ? `🏆 ${row.shownName}` : row.shownName, x + 100, top + 40);
        ctx.fillStyle = t.muted;
        setBodyFont(22, 400);
        ctx.fillText(row.shownComment, x + 100, top + 72);
        ctx.fillStyle = `${t.text}14`;
        ctx.fillRect(x + 100, top + 91, w - 132, 1);
      },
    });

    // The real Start! button belongs to the host; this just shows the show is about to begin.
    ctx.fillStyle = gradient(WIDTH / 2 - 150, WIDTH / 2 + 150);
    roundRect(ctx, WIDTH / 2 - 150, 1150, 300, 76, 38);
    ctx.fill();
    ctx.fillStyle = t.accentFg;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `800 34px ${DISPLAY}`;
    ctx.fillText('Start!', WIDTH / 2, 1189);
    ctx.textBaseline = 'alphabetic';
  }

  function drawWinners(now) {
    drawCreatorHeader();

    const scene = winners ?? { items: [], total: 0 };
    const { tops, contentHeight } = stack(scene.items);
    const cardBottom = drawListCard({
      title: '🏆 Winners',
      total: scene.total,
      items: scene.items,
      tops,
      contentHeight,
      since: phaseSince,
      now,
      hug: true,
      empty: 'No winners yet.',
      drawItem(item, x, top, w) {
        ctx.textBaseline = 'alphabetic';
        ctx.textAlign = 'left';
        if (item.kind === 'group') {
          ctx.fillStyle = `${t.accent}26`;
          ctx.fillRect(x, top + 8, w, 48);
          ctx.fillStyle = t.accent;
          setBodyFont(26, 800);
          ctx.fillText(item.shown, x + 32, top + 41);
          if (item.prize) {
            ctx.textAlign = 'right';
            ctx.fillStyle = t.muted;
            setBodyFont(22, 600);
            ctx.fillText(oneLine(ctx, item.prize, 200), x + w - 32, top + 41);
          }
          return;
        }
        ctx.beginPath();
        ctx.arc(x + 54, top + 49, 26, 0, TAU);
        ctx.fillStyle = gradient(x + 28, x + 80);
        ctx.fill();
        ctx.fillStyle = t.accentFg;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `800 26px ${DISPLAY}`;
        ctx.fillText(String(item.rank), x + 54, top + 51);
        ctx.textBaseline = 'alphabetic';
        ctx.textAlign = 'left';
        ctx.fillStyle = t.text;
        setBodyFont(26, 700);
        ctx.fillText(item.shownName, x + 100, top + 44);
        ctx.fillStyle = t.muted;
        setBodyFont(22, 400);
        ctx.fillText(item.shownComment, x + 100, top + 78);
      },
    });

    if (scene.items.length) {
      ctx.textAlign = 'center';
      ctx.fillStyle = t.muted;
      setBodyFont(28, 600);
      ctx.fillText('Congratulations to all the winners! 🎉', WIDTH / 2, Math.min(HEIGHT - 90, cardBottom + 70));
    }
  }

  function drawStage(now) {
    // Creator strip + the prize being drawn
    ctx.textAlign = 'center';
    ctx.fillStyle = t.muted;
    setBodyFont(24, 600);
    ctx.fillText(oneLine(ctx, creator.title, WIDTH - MARGIN * 2), WIDTH / 2, 80);
    if (tier) {
      ctx.fillStyle = t.text;
      fitFont(ctx, tier.name, 800, DISPLAY, 54, 30, WIDTH - MARGIN * 2);
      ctx.fillText(oneLine(ctx, tier.name, WIDTH - MARGIN * 2), WIDTH / 2, 160);
      ctx.fillStyle = t.accent2;
      setBodyFont(26, 600);
      ctx.fillText(oneLine(ctx, tier.detail, WIDTH - MARGIN * 2), WIDTH / 2, 206);
    }

    if (stage?.mode === 'wheel') drawWheel();
    else if (stage?.mode === 'countdown') drawCountdown();
    else if (stage?.mode === 'slot') drawSlot();

    if (reveal) drawReveal(now);
  }

  function drawSlot() {
    const windowH = SLOT_ROW * 3;
    const top = STAGE_MID - windowH / 2;
    const centreY = top + windowH / 2;
    ctx.fillStyle = `${t.text}10`;
    roundRect(ctx, MARGIN, top, WIDTH - MARGIN * 2, windowH, 32);
    ctx.fill();
    ctx.fillStyle = `${t.accent}22`;
    ctx.fillRect(MARGIN, centreY - SLOT_ROW / 2, WIDTH - MARGIN * 2, SLOT_ROW);

    ctx.save();
    roundRect(ctx, MARGIN, top, WIDTH - MARGIN * 2, windowH, 32);
    ctx.clip();
    for (const { row, offset } of visibleSlotRows(stage.rows, stage.centre, 2)) {
      const y = centreY + offset * SLOT_ROW;
      const strength = Math.max(0, 1 - Math.abs(offset) * 0.55);
      ctx.globalAlpha = Math.max(0.15, strength);
      const win = stage.landed && row.winner;
      ctx.fillStyle = win ? t.accent : row.placeholder ? t.muted : t.text;
      fitFont(ctx, row.name, 800, DISPLAY, 54, 26, WIDTH - MARGIN * 2 - 60);
      ctx.fillText(oneLine(ctx, row.name, WIDTH - MARGIN * 2 - 60), WIDTH / 2, y + 4);
      if (row.user) {
        ctx.fillStyle = t.muted;
        setBodyFont(26, 500);
        ctx.fillText(oneLine(ctx, row.user, WIDTH - MARGIN * 2 - 60), WIDTH / 2, y + 46);
      }
    }
    ctx.restore();
    ctx.strokeStyle = `${t.accent}88`;
    ctx.lineWidth = 3;
    roundRect(ctx, MARGIN, top, WIDTH - MARGIN * 2, windowH, 32);
    ctx.stroke();
  }

  function drawWheel() {
    const size = 620;
    const x = (WIDTH - size) / 2;
    const y = STAGE_MID - size / 2;
    if (wheelCanvas.width > 0 && wheelCanvas.height > 0) ctx.drawImage(wheelCanvas, x, y, size, size);
    // The pointer is a CSS element on the page, so draw our own.
    ctx.beginPath();
    ctx.moveTo(WIDTH / 2 - 26, y - 8);
    ctx.lineTo(WIDTH / 2 + 26, y - 8);
    ctx.lineTo(WIDTH / 2, y + 48);
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = 12;
    ctx.fill();
    ctx.shadowBlur = 0;
  }

  function drawCountdown() {
    const cx = WIDTH / 2;
    const r = 210;
    const cy = STAGE_MID - 75; // ring + name + handle, centred as one block
    ctx.lineWidth = 22;
    ctx.lineCap = 'round';
    ctx.strokeStyle = `${t.text}1a`;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, TAU);
    ctx.stroke();
    const { start, end } = ringSweep(stage.fraction);
    if (end > start) {
      ctx.strokeStyle = stage.landed ? t.accent : gradient(cx - r, cx + r);
      ctx.beginPath();
      ctx.arc(cx, cy, r, start, end);
      ctx.stroke();
    }
    ctx.lineCap = 'butt';
    ctx.fillStyle = stage.landed ? t.accent : t.text;
    ctx.font = `800 190px ${DISPLAY}`;
    ctx.textBaseline = 'middle';
    ctx.fillText(stage.number, cx, cy + 6);
    ctx.textBaseline = 'alphabetic';

    ctx.fillStyle = stage.landed ? t.accent : t.text;
    fitFont(ctx, stage.name, 800, DISPLAY, 52, 26, WIDTH - MARGIN * 2);
    ctx.fillText(oneLine(ctx, stage.name, WIDTH - MARGIN * 2), cx, cy + 320);
    ctx.fillStyle = t.muted;
    setBodyFont(28, 500);
    ctx.fillText(oneLine(ctx, stage.user, WIDTH - MARGIN * 2), cx, cy + 366);
  }

  function drawReveal(now) {
    const k = Math.min(1, (now - revealSince) / REVEAL_FADE_MS);
    const ease = 1 - (1 - k) ** 3;
    const w = WIDTH - MARGIN * 2;
    const x = MARGIN;
    setBodyFont(28, 400);
    const comment = reveal.comment ? wrapText(ctx, `“${reveal.comment}”`, w - 100, 4) : [];
    const h = 290 + comment.length * 44 + (comment.length ? 36 : 0);
    const y = STAGE_MID - h / 2 + (1 - ease) * 40;
    ctx.save();
    // Dim the reel behind so the card reads on its own, like the page's reveal backdrop.
    ctx.globalAlpha = ease * 0.85;
    const scrim = ctx.createLinearGradient(0, STAGE_TOP, 0, STAGE_TOP + 80); // fade in, no hard edge
    scrim.addColorStop(0, `${t.bg}00`);
    scrim.addColorStop(1, t.bg);
    ctx.fillStyle = scrim;
    ctx.fillRect(0, STAGE_TOP, WIDTH, HEIGHT - STAGE_TOP);
    ctx.globalAlpha = ease;
    ctx.fillStyle = t.bg;
    roundRect(ctx, x, y, w, h, 36);
    ctx.fill();
    ctx.fillStyle = `${t.accent}22`;
    ctx.fill();
    ctx.strokeStyle = t.accent;
    ctx.lineWidth = 4;
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.fillStyle = t.accent;
    ctx.font = `800 30px ${DISPLAY}`;
    ctx.fillText('🏆  WINNER', WIDTH / 2, y + 62);
    ctx.fillStyle = t.muted;
    setBodyFont(24, 600);
    ctx.fillText(oneLine(ctx, [reveal.tierName, reveal.prize].filter(Boolean).join(' — '), w - 60), WIDTH / 2, y + 104);
    ctx.fillStyle = t.text;
    fitFont(ctx, reveal.displayName, 800, DISPLAY, 66, 32, w - 60);
    ctx.fillText(oneLine(ctx, reveal.displayName, w - 60), WIDTH / 2, y + 190);
    ctx.fillStyle = t.accent2;
    setBodyFont(32, 600);
    ctx.fillText(oneLine(ctx, `@${reveal.username}`, w - 60), WIDTH / 2, y + 242);
    ctx.fillStyle = t.text;
    setBodyFont(28, 400);
    comment.forEach((line, i) => ctx.fillText(line, WIDTH / 2, y + 320 + i * 44));
    ctx.restore();
  }

  /* ---------------------------- confetti ---------------------------- */
  function updateParticles(dt) {
    if (!dt) return;
    for (const p of particles) {
      p.vy += 1400 * dt;
      p.vx *= 1 - 0.8 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.spin += 8 * dt;
    }
    particles = particles.filter((p) => p.y < HEIGHT + 40);
  }

  function drawParticles() {
    for (const p of particles) {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.spin);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      ctx.restore();
    }
  }

  return api;
}
